/**
 * Creates a Date object in the specified timezone from date and time strings
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {string} timeString - Time in HH:MM format
 * @param {string} timezone - Target timezone (e.g., 'America/New_York')
 * @returns {Date} Date object representing the correct UTC time
 */
export const createDateInTimezone = (dateString, timeString, timezone) => {
  if (!dateString || !timeString || !timezone) {
    throw new Error('Date, time, and timezone are required');
  }

  try {
    // Create a date string that represents the local time in the target timezone
    const dateTimeString = `${dateString}T${timeString}:00`;

    // Use a more reliable approach with Intl.DateTimeFormat
    // First, create a date assuming it's local time
    const localDate = new Date(dateTimeString);

    // Check if the date is valid
    if (isNaN(localDate.getTime())) {
      throw new Error('Invalid date or time format');
    }

    // Get what this date/time would be in the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(localDate);
    const targetYear = parts.find(p => p.type === 'year').value;
    const targetMonth = parts.find(p => p.type === 'month').value;
    const targetDay = parts.find(p => p.type === 'day').value;
    const targetHour = parts.find(p => p.type === 'hour').value;
    const targetMinute = parts.find(p => p.type === 'minute').value;

    // Calculate the offset between what we want and what we got
    const wantedTime = new Date(`${dateString}T${timeString}:00`).getTime();
    const targetTime = new Date(`${targetYear}-${targetMonth}-${targetDay}T${targetHour}:${targetMinute}:00`).getTime();
    const offset = wantedTime - targetTime;

    // Apply the offset to get the correct UTC time
    const correctDate = new Date(localDate.getTime() + offset);

    // Validate the result
    if (isNaN(correctDate.getTime())) {
      throw new Error('Failed to create valid date');
    }

    return correctDate;
  } catch (error) {
    console.error('Error creating date in timezone:', error, { dateString, timeString, timezone });
    // Fallback to simple local time parsing
    const fallbackDate = new Date(`${dateString}T${timeString}:00`);
    if (isNaN(fallbackDate.getTime())) {
      throw new Error('Invalid date/time values provided');
    }
    return fallbackDate;
  }
};

/**
 * Gets the timezone offset for a specific timezone at a given date
 * @param {Date} date - The date to check
 * @param {string} timezone - The timezone to get offset for
 * @returns {number} Offset in minutes
 */
const getTimezoneOffset = (date, timezone) => {
  // Create two dates: one in UTC, one in the target timezone
  const utcDate = new Date(date.toLocaleString('en-CA', { timeZone: 'UTC' }));
  const targetDate = new Date(date.toLocaleString('en-CA', { timeZone: timezone }));
  
  // Calculate the offset in minutes
  return (targetDate.getTime() - utcDate.getTime()) / (1000 * 60);
};

/**
 * Formats a date for display in a specific timezone
 * @param {Date|string} date - Date to format
 * @param {string} timezone - Timezone for display
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, timezone, options = {}) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const defaultOptions = {
    timeZone: timezone || 'UTC',
    ...options
  };
  
  return dateObj.toLocaleString('en-US', defaultOptions);
};

/**
 * Gets the user's current timezone
 * @returns {string} User's timezone
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Validates if a timezone string is valid
 * @param {string} timezone - Timezone to validate
 * @returns {boolean} True if valid
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch (error) {
    return false;
  }
};